import { useEffect } from 'react';
import { styled } from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { Link, RouteComponentProps } from '@reach/router';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { useAuth } from '@calefy-inc/authentication';
import { DisplayInlineError } from '../../common/ErrorHandling';
import { AdminPolicyStreamPlansTable } from './AdminPolicyStreamPlansTable';
import type { AdminPolicystreamPlansQuery } from '../../../gql/graphql';
import { graphql } from '../../../gql';

const PAGE_SIZE = 20;

const planDetailPath = (planId: string) => {
  return `/insurtech/admin/policystream/plans/${planId}`;
};

interface PolicyStreamPlansAdminPageProps extends RouteComponentProps {}

export const PolicyStreamPlansAdminPage = (
  _props: PolicyStreamPlansAdminPageProps,
) => {
  const { token } = useAuth();
  const [runQuery, { data, loading, error, fetchMore, networkStatus }] =
    useLazyQuery(ADMIN_POLICYSTREAM_PLANS_PAGE, {
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    if (token) {
      runQuery({
        variables: { token, first: PAGE_SIZE },
      });
    }
  }, [token, runQuery]);

  const connection = data?.policystreamPlans;
  const showBottomLoader =
    loading && (!connection || networkStatus === NetworkStatus.fetchMore);
  const plans =
    connection?.edges
      ?.map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => Boolean(node)) ?? [];

  const loadMore = () => {
    if (!token || !connection?.pageInfo?.endCursor) {
      return;
    }
    fetchMore({
      variables: {
        token,
        first: PAGE_SIZE,
        after: connection.pageInfo.endCursor,
      },
      updateQuery: (
        previous: AdminPolicystreamPlansQuery,
        { fetchMoreResult },
      ) => {
        if (!fetchMoreResult) {
          return previous;
        }
        const nextConn = fetchMoreResult.policystreamPlans;
        if (!nextConn) {
          return previous;
        }
        return {
          policystreamPlans: {
            ...nextConn,
            edges: [
              ...(previous.policystreamPlans?.edges ?? []),
              ...(nextConn.edges ?? []),
            ],
          },
        };
      },
    });
  };

  if (!token) {
    return <Typography>Sign in to view PolicyStream plans.</Typography>;
  }

  return (
    <PageSection>
      <Typography variant='h5' component='h1' gutterBottom>
        PolicyStream plans
      </Typography>
      <BackLink to='/insurtech/admin/policystream'>
        &larr; PolicyStream admin
      </BackLink>

      <DisplayInlineError error={error} />

      <AdminPolicyStreamPlansTable
        plans={plans}
        planDetailPathForId={planDetailPath}
      />

      {connection?.pageInfo?.hasNextPage ? (
        <LoadMoreWrap>
          <Button
            type='button'
            variant='outlined'
            onClick={loadMore}
            disabled={loading && Boolean(connection)}
          >
            Load {PAGE_SIZE} more
          </Button>
        </LoadMoreWrap>
      ) : null}

      {showBottomLoader ? (
        <TableLoadingFooter>
          <CircularProgress size={24} />
          <LoadingHint>{connection ? 'Loading more…' : 'Loading…'}</LoadingHint>
        </TableLoadingFooter>
      ) : null}
    </PageSection>
  );
};

const PageSection = styled('section')(({ theme }) => {
  return { paddingTop: theme.spacing(2) };
});

const BackLink = styled(Link)(({ theme }) => {
  return {
    display: 'inline-block',
    marginBottom: theme.spacing(2),
  };
});

const LoadMoreWrap = styled('div')(({ theme }) => {
  return { marginTop: theme.spacing(2) };
});

const TableLoadingFooter = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  };
});

const LoadingHint = styled('span')(({ theme }) => {
  return {
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
  };
});

const ADMIN_POLICYSTREAM_PLANS_PAGE = graphql(`
  query AdminPolicystreamPlans($token: String!, $first: Int, $after: String) {
    policystreamPlans(token: $token, first: $first, after: $after) {
      edges {
        cursor
        node {
          ...AdminPolicyStreamPlanTableFields
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);
